import type { JSX } from 'react';

import type { Preisauskunft as Auskunft } from '../api/buchungen';
import { alsEuro } from '../betrag';

/**
 * Was die Buchung kostet — vor dem Buchen (BUC-80, BUC-102, BUC-120).
 *
 * BUC-80 nennt die Preisauskunft als einen der fünf Teile der Aufnahmemaske.
 * Bis zum 19.09.2026 fehlte sie: Wer am Telefon nach dem Preis gefragt wurde,
 * musste buchen, um ihn zu sehen.
 *
 * DIE FÜHRENDE ZAHL ENTSCHEIDET DER SERVER, nicht diese Anzeige. Sie kommt
 * als `preisfuehrung` mit — brutto beim Verbraucher, netto beim
 * Geschäftskunden (BUC-120). Die andere steht daneben, weil beide im
 * Gespräch vorkommen: „inklusive Mehrwertsteuer" fragt jeder.
 *
 * GERECHNET WIRD HIER NICHTS (RND-01). Jede Zahl steht so, wie der Server sie
 * geliefert hat; eine Multiplikation im Browser wäre die zweite Rechnung — und
 * bei Rundung je Position die falsche (RND-05).
 */
export function Preisauskunft({
  auskunft,
  laeuft,
}: {
  readonly auskunft: Auskunft | null;
  readonly laeuft: boolean;
}): JSX.Element {
  if (auskunft === null) {
    return (
      <p className="feld__hilfe" aria-live="polite">
        {laeuft ? 'Preis wird geholt …' : 'Der Preis steht, sobald Kunde, Produkt und Termin gewählt sind.'}
      </p>
    );
  }

  if (auskunft.preis === null) {
    return (
      <p className="hinweiszeile" role="status">
        Für dieses Produkt ist zum {new Date(auskunft.stichtag).toLocaleDateString('de-DE')} kein
        Preis hinterlegt. Buchen ist möglich; der Auftrag entsteht dann ohne Betrag und
        muss von Hand bepreist werden.
      </p>
    );
  }

  const p = auskunft.preis;
  const brutto = auskunft.preisfuehrung === 'brutto';

  return (
    <section className="karte karte--zweit preisauskunft" aria-label="Preisauskunft">
      <h3>Preis</h3>
      <p className="preisauskunft__summe">
        <strong>{alsEuro(brutto ? p.brutto : p.netto)}</strong>{' '}
        <span className="kanalliste__name">
          {brutto ? 'brutto' : 'netto'} für {auskunft.plaetze}{' '}
          {auskunft.plaetze === 1 ? 'Platz' : 'Plätze'}
        </span>
      </p>
      {/* `.angaben` ist ein Raster aus dt und dd — ohne Huellen dazwischen. */}
      <dl className="angaben">
        <dt>Je Platz</dt>
        <dd>
          {alsEuro(p.einzelpreis)}
          {p.staffelpreis !== null && p.abMenge !== null && (
            <span className="kanalliste__name"> · Staffel ab {p.abMenge}</span>
          )}
        </dd>
        <dt>Netto</dt>
        <dd>{alsEuro(p.netto)}</dd>
        <dt>Umsatzsteuer ({p.steuerProzent} %)</dt>
        <dd>{alsEuro(p.steuerbetrag)}</dd>
        <dt>Brutto</dt>
        <dd>{alsEuro(p.brutto)}</dd>
      </dl>
      <p className="feld__hilfe">
        Preisliste {p.preisliste}
        {p.festpreisAus !== null && ` · Festpreis aus ${p.festpreisAus}`}
        {Number(p.vorteil) > 0 && ` · Vorteil ${alsEuro(p.vorteil)} (${p.vorteilProzent} %)`}
        {' · '}Steuerschlüssel {p.steuerschluessel}
        {' · '}Stand {new Date(auskunft.stichtag).toLocaleDateString('de-DE')}
      </p>
      {/*
        RND-18: Ein gepflegter Bruttobetrag, der mit diesem Steuersatz nicht
        darstellbar ist, wird auf den nächstliegenden gerundet. Das gehört vor
        die Zusage am Telefon und nicht in eine Fußnote.
      */}
      {!p.darstellbar && (
        <p className="hinweiszeile" role="status">
          Der gepflegte Bruttopreis ist mit diesem Steuersatz nicht genau darstellbar; genannt wird
          der nächstliegende Betrag.
        </p>
      )}
      {/*
        Die Zusatzleistungen zählen hier NICHT mit: Sie werden erst an der
        Buchung gewählt (BUC-119). Ein Betrag, der sie stillschweigend
        ausließe, wäre am Telefon eine zu niedrige Zusage.
      */}
      <p className="feld__hilfe">
        Ohne Zusatzleistungen — die werden nach dem Buchen an der Buchung gewählt.
      </p>
    </section>
  );
}
